// src/services/enhancedProductService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import {
  Product,
  ProductFormData,
  ProductWithDetails,
  ProductVariant,
  WarehouseAllocation,
  Warehouse,
} from '../../../enums/product';
import { CategoryService } from '../category/categoryService';
import { WarehouseService } from '../warehouse/warehouseService';
import { WarehouseInventoryService } from './WarehouseInventoryService';

const PRODUCTS_COLLECTION = 'products';

export interface PaginatedProductsResult {
  products: Product[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

interface InventoryRecord {
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  availableSerials?: string[];
}

type InventoryMap = Map<string, InventoryRecord>;
type ActiveWarehouse = Warehouse & { id: string };

export class ProductService {

  private static inventoryKey(
    warehouseId: string,
    productId: string,
    variantId?: string | null
  ): string {
    return variantId
      ? `${warehouseId}::${productId}::${variantId}`
      : `${warehouseId}::${productId}`;
  }

  private static async getActiveWarehousesTyped(): Promise<ActiveWarehouse[]> {
    const all = await WarehouseService.getActiveWarehouses();
    return all.filter((w): w is ActiveWarehouse => !!w.id);
  }

  private static async batchFetchInventory(
    productIds: string[],
    warehouses: ActiveWarehouse[]
  ): Promise<InventoryMap> {
    const map: InventoryMap = new Map();
    if (productIds.length === 0 || warehouses.length === 0) return map;

    const productIdSet = new Set(productIds);

    const warehouseResults = await Promise.all(
      warehouses.map(async (warehouse) => {
        const rows = await WarehouseInventoryService.getWarehouseInventory(warehouse.id);
        return { warehouseId: warehouse.id, rows };
      })
    );

    for (const { warehouseId, rows } of warehouseResults) {
      for (const row of rows) {
        if (!productIdSet.has(row.productId)) continue;
        const key = this.inventoryKey(warehouseId, row.productId, row.variantId);
        const existing = map.get(key);
        if (existing) {
          existing.quantity += row.quantity;
          if (row.availableSerials) {
            existing.availableSerials = [...(existing.availableSerials || []), ...row.availableSerials];
          }
        } else {
          map.set(key, {
            quantity: row.quantity,
            minStockLevel: row.minStockLevel,
            maxStockLevel: row.maxStockLevel,
            isLowStock: row.isLowStock,
            isOutOfStock: row.isOutOfStock,
            isOverStock: row.isOverStock,
            availableSerials: row.availableSerials,
          });
        }
      }
    }

    return map;
  }

  // private static enrichProductFromMap(
  //   product: Product,
  //   warehouses: ActiveWarehouse[],
  //   inventoryMap: InventoryMap
  // ): Product {
  //   if (!product.id) return product;

  //   if (!product.hasVariants) {
  //     const warehouseAllocations: WarehouseAllocation[] = [];
  //     let totalStock = 0;

  //     for (const warehouse of warehouses) {
  //       const key = this.inventoryKey(warehouse.id, product.id);
  //       const inv = inventoryMap.get(key);
  //       if (inv) {
  //         warehouseAllocations.push({
  //           warehouseId: warehouse.id,
  //           warehouseName: warehouse.name,
  //           quantity: inv.quantity,
  //           minStockLevel: inv.minStockLevel,
  //           maxStockLevel: inv.maxStockLevel,
  //           isLowStock: inv.isLowStock,
  //           isOutOfStock: inv.isOutOfStock,
  //           isOverStock: inv.isOverStock,
  //         });
  //         totalStock += inv.quantity;
  //       }
  //     }

  //     return {
  //       ...product,
  //       warehouseAllocations,
  //       stockQuantity: totalStock,
  //       isOutOfStock: totalStock === 0,
  //       isLowStock: totalStock > 0 && totalStock <= product.minStockLevel,
  //     };
  //   } else {
  //     const enrichedVariants: ProductVariant[] = [];
  //     let totalStock = 0;

  //     for (const variant of product.variants) {
  //       const warehouseAllocations: WarehouseAllocation[] = [];
  //       let variantTotalStock = 0;

  //       for (const warehouse of warehouses) {
  //         const key = this.inventoryKey(warehouse.id, product.id, variant.id);
  //         const inv = inventoryMap.get(key);
  //         if (inv) {
  //           warehouseAllocations.push({
  //             warehouseId: warehouse.id,
  //             warehouseName: warehouse.name,
  //             quantity: inv.quantity,
  //             minStockLevel: inv.minStockLevel,
  //             maxStockLevel: inv.maxStockLevel,
  //             isLowStock: inv.isLowStock,
  //             isOutOfStock: inv.isOutOfStock,
  //             isOverStock: inv.isOverStock,
  //           });
  //           variantTotalStock += inv.quantity;
  //         }
  //       }

  //       enrichedVariants.push({
  //         ...variant,
  //         warehouseAllocations,
  //         stockQuantity: variantTotalStock,
  //         isOutOfStock: variantTotalStock === 0,
  //         isLowStock: variantTotalStock > 0 && variantTotalStock <= variant.minStockLevel,
  //         isOverStock: variantTotalStock > 0 && variantTotalStock >= variant.maxStockLevel,
  //       });

  //       totalStock += variantTotalStock;
  //     }

  //     return {
  //       ...product,
  //       variants: enrichedVariants,
  //       stockQuantity: totalStock,
  //       isOutOfStock: totalStock === 0,
  //       isLowStock: totalStock > 0 && totalStock <= product.minStockLevel,
  //     };
  //   }
  // }

  private static enrichProductFromMap(
  product: Product,
  warehouses: ActiveWarehouse[],
  inventoryMap: InventoryMap
): Product {
  if (!product.id) return product;

  if (!product.hasVariants) {
    const warehouseAllocations: WarehouseAllocation[] = [];
    let totalStock = 0;

    for (const warehouse of warehouses) {
      const key = this.inventoryKey(warehouse.id, product.id);
      const inv = inventoryMap.get(key);
      if (inv) {
        const warehouseQty = inv.quantity;
        const isOut  = warehouseQty === 0;
        const isOver = warehouseQty > 0 && warehouseQty >= inv.maxStockLevel;
        const isLow  = warehouseQty > 0 && warehouseQty <= inv.minStockLevel && !isOver;

        warehouseAllocations.push({
          warehouseId: warehouse.id,
          warehouseName: warehouse.name,
          quantity: warehouseQty,
          minStockLevel: inv.minStockLevel,
          maxStockLevel: inv.maxStockLevel,
          isLowStock: isLow,
          isOutOfStock: isOut,
          isOverStock: isOver,
          serialNumbers: inv.availableSerials,
        });
        totalStock += warehouseQty;
      }
    }

    return {
      ...product,
      warehouseAllocations,
      stockQuantity: totalStock,
      isOutOfStock: totalStock === 0,
      isLowStock: totalStock > 0 && warehouseAllocations.some(a => a.isLowStock),
    };

  } else {
    const enrichedVariants: ProductVariant[] = [];
    let totalStock = 0;

    for (const variant of product.variants) {
      const warehouseAllocations: WarehouseAllocation[] = [];
      let variantTotalStock = 0;

      for (const warehouse of warehouses) {
        const key = this.inventoryKey(warehouse.id, product.id, variant.id);
        const inv = inventoryMap.get(key);
        if (inv) {
          const warehouseQty = inv.quantity;
          const isOut  = warehouseQty === 0;
          const isOver = warehouseQty > 0 && warehouseQty >= inv.maxStockLevel;
          const isLow  = warehouseQty > 0 && warehouseQty <= inv.minStockLevel && !isOver;

          warehouseAllocations.push({
            warehouseId: warehouse.id,
            warehouseName: warehouse.name,
            quantity: warehouseQty,
            minStockLevel: inv.minStockLevel,
            maxStockLevel: inv.maxStockLevel,
            isLowStock: isLow,
            isOutOfStock: isOut,
            isOverStock: isOver,
          });
          variantTotalStock += warehouseQty;
        }
      }

      enrichedVariants.push({
        ...variant,
        warehouseAllocations,
        stockQuantity: variantTotalStock,
        isOutOfStock: variantTotalStock === 0,
        isOverStock: warehouseAllocations.some(a => a.isOverStock),
        isLowStock: variantTotalStock > 0 && warehouseAllocations.some(a => a.isLowStock),
      });

      totalStock += variantTotalStock;
    }

    return {
      ...product,
      variants: enrichedVariants,
      stockQuantity: totalStock,
      isOutOfStock: totalStock === 0,
      isLowStock: totalStock > 0 && enrichedVariants.some(v => v.isLowStock),
    };
  }
}

  private static async enrichProductWithInventory(product: Product): Promise<Product> {
    try {
      if (!product.id) return product;
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory([product.id], warehouses);
      return this.enrichProductFromMap(product, warehouses, inventoryMap);
    } catch (error) {
      console.error('Error enriching product with inventory:', error);
      return product;
    }
  }

  /* ===================== GET PRODUCTS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getProductsPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    categoryFilter?: string,
    stockFilter?: string,
    variantFilter?: string,
    featuredFilter?: string,
    brandFilter?: string
  ): Promise<PaginatedProductsResult> {
    try {
      const collectionRef = collection(db, PRODUCTS_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];

      if (categoryFilter && categoryFilter !== 'all') {
        filterConstraints.push(where('categoryId', '==', categoryFilter));
      }
      if (variantFilter === 'withVariants') {
        filterConstraints.push(where('hasVariants', '==', true));
      } else if (variantFilter === 'withoutVariants') {
        filterConstraints.push(where('hasVariants', '==', false));
      }
      if (featuredFilter === 'featured') {
        filterConstraints.push(where('isFeatured', '==', true));
      } else if (featuredFilter === 'nonFeatured') {
        filterConstraints.push(where('isFeatured', '==', false));
      }
      if (brandFilter && brandFilter !== 'all') {
        filterConstraints.push(where('brandId', '==', brandFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map raw products ───────────────────────────────────────────────────
      const rawProducts: Product[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name || '',
          description: data.description || '',
          price: data.price || 0,
          discount: data.discount || 0,
          unitCost: data.unitCost || 0,
          isOutOfStock: false,
          categoryId: data.categoryId || '',
          subcategoryId: data.subcategoryId || null,
          brandId: data.brandId || null,
          sku: data.sku || '',
          images: data.images || [],
          variants: data.variants || [],
          hasVariants: data.hasVariants || false,
          isFeatured: data.isFeatured || false,
          stockQuantity: 0,
          minStockLevel: data.minStockLevel || 0,
          maxStockLevel: data.maxStockLevel || 0,
          isLowStock: false,
          warehouseAllocations: [],
          tags: data.tags || [],
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy,
        } as Product;
      });

      if (rawProducts.length === 0) {
        return {
          products: [],
          totalCount,
          lastVisible: null,
          firstVisible: null,
        };
      }

      // ── Enrich with inventory, then apply stock + search filters ──────────
      const warehouses = await this.getActiveWarehousesTyped();
      const productIds = rawProducts.map(p => p.id as string);
      const inventoryMap = await this.batchFetchInventory(productIds, warehouses);

      const products: Product[] = [];
      for (const raw of rawProducts) {
        const enriched = this.enrichProductFromMap(raw, warehouses, inventoryMap);

        if (stockFilter === 'inStock'    && enriched.isOutOfStock)  continue;
        if (stockFilter === 'outOfStock' && !enriched.isOutOfStock) continue;

        if (searchQuery && searchQuery.trim() !== '') {
          const lower = searchQuery.toLowerCase();
          const matches =
            enriched.name.toLowerCase().includes(lower) ||
            enriched.description.toLowerCase().includes(lower) ||
            enriched.sku.toLowerCase().includes(lower) ||
            enriched.variants.some(v => v.sku.toLowerCase().includes(lower)) ||
            (enriched.tags || []).some(t => t.toLowerCase().includes(lower));
          if (!matches) continue;
        }

        products.push(enriched);
      }

      return {
        products,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated products:', error);
      throw error;
    }
  }

  /* ===================== IMAGE HELPERS ===================== */

  static async uploadImages(files: File[], productId?: string): Promise<string[]> {
    return Promise.all(
      files.map(async (file) => {
        const timestamp = Date.now();
        const storageRef = ref(storage, `products/${productId || 'temp'}/${timestamp}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      })
    );
  }

  static async deleteImages(imageUrls: string[]): Promise<void> {
    await Promise.all(
      imageUrls.map(async (url) => {
        try {
          await deleteObject(ref(storage, url));
        } catch (error) {
          console.warn('Error deleting image:', error);
        }
      })
    );
  }

  /* ===================== CREATE ===================== */

  static async createProduct(formData: ProductFormData, userId: string): Promise<string> {
    try {
      const category = await CategoryService.getCategory(formData.categoryId);
      if (!category) throw new Error('Selected category does not exist');

      if (formData.subcategoryId) {
        const subcategory = await CategoryService.getCategory(formData.subcategoryId);
        if (!subcategory) throw new Error('Selected subcategory does not exist');
      }

      const discount = parseFloat(formData.discount) || 0;
      if (discount < 0 || discount > 100) throw new Error('Discount must be between 0 and 100');

      const productData = {
        name: formData.name,
        description: formData.description,
        price: formData.hasVariants ? 0 : parseFloat(formData.price),
        discount: formData.hasVariants ? 0 : discount,
        unitCost: formData.hasVariants ? 0 : parseFloat(formData.unitCost) || 0,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null,
        brandId: formData.brandId || null,
        sku: formData.hasVariants ? '' : formData.sku,
        images: [] as string[],
        variants: formData.hasVariants
          ? formData.variants.map(v => ({
              id: v.id,
              variantSelections: v.variantSelections,
              price: v.price,
              discount: v.discount || 0,
              unitCost: v.unitCost || 0,
              sku: v.sku,
              isDefault: v.isDefault,
              minStockLevel: v.minStockLevel,
              maxStockLevel: v.maxStockLevel,
              requiresSerialTracking: v.requiresSerialTracking || false,
            }))
          : [],
        hasVariants: formData.hasVariants,
        requiresSerialTracking: formData.hasVariants ? false : (formData.requiresSerialTracking || false),
        isFeatured: false,
        minStockLevel: parseFloat(formData.minStockLevel) || 0,
        maxStockLevel: parseFloat(formData.maxStockLevel) || 0,
        tags: (formData.tags || []).map(t => t.trim()).filter(Boolean),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      };

      const productRef = await addDoc(collection(db, PRODUCTS_COLLECTION), productData);
      const productId = productRef.id;

      if (formData.images.length > 0) {
        const imageUrls = await this.uploadImages(formData.images, productId);
        await updateDoc(productRef, { images: imageUrls });
      }

      if (!formData.hasVariants) {
        await this.processSimpleProductAllocations(
          productId, formData.name, formData.price, formData.sku,
          formData.warehouseAllocations, discount, formData.unitCost,
          formData.requiresSerialTracking
        );
      } else {
        await this.processVariantProductAllocations(productId, formData.name, formData.variants);
      }

      return productId;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  private static async processSimpleProductAllocations(
    productId: string, productName: string, price: string, sku: string,
    allocations: WarehouseAllocation[], discount?: number, unitCost?: string,
    requiresSerialTracking?: boolean
  ): Promise<void> {
    const avgCost = parseFloat(unitCost || '') || 0;
    await Promise.all(
      allocations.map(allocation =>
        WarehouseInventoryService.allocateProductToWarehouse(
          allocation.warehouseId, productId, productName, allocation.quantity,
          allocation.minStockLevel, allocation.maxStockLevel, sku,
          parseFloat(price), undefined, undefined, discount, avgCost,
          requiresSerialTracking ? (allocation.serialNumbers || []) : undefined
        )
      )
    );
  }

  private static async processVariantProductAllocations(
    productId: string, productName: string, variants: ProductVariant[]
  ): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const variant of variants) {
      if (!variant.warehouseAllocations) continue;
      const variantName = variant.variantSelections.map(vs => vs.variantValue).join(' - ');
      for (const allocation of variant.warehouseAllocations) {
        promises.push(
          WarehouseInventoryService.allocateProductToWarehouse(
            allocation.warehouseId, productId, productName, allocation.quantity,
            allocation.minStockLevel, allocation.maxStockLevel, variant.sku,
            variant.price, variant.id, variantName, variant.discount, variant.unitCost,
            variant.requiresSerialTracking ? (allocation.serialNumbers || []) : undefined
          )
        );
      }
    }
    await Promise.all(promises);
  }

  /* ===================== FEATURED ===================== */

  static async toggleFeaturedProduct(productId: string, isFeatured: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        isFeatured, updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  }

  static async getFeaturedProducts(): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION),
        where('isFeatured', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  }

  static async getFeaturedProductsWithDetails(): Promise<ProductWithDetails[]> {
    try {
      const products = await this.getFeaturedProducts();
      return Promise.all(
        products.map(async product => {
          const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
          const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
          return { ...product, category: category || undefined, subcategory: subcategory || undefined };
        })
      );
    } catch (error) {
      console.error('Error fetching featured products with details:', error);
      throw error;
    }
  }

  /* ===================== UPDATE ===================== */

  static async updateProduct(
    productId: string, formData: ProductFormData, existingImages: string[] = []
  ): Promise<void> {
    try {
      const currentProduct = await this.getProduct(productId);
      if (!currentProduct) throw new Error('Product not found');

      const category = await CategoryService.getCategory(formData.categoryId);
      if (!category) throw new Error('Selected category does not exist');

      if (formData.subcategoryId) {
        const subcategory = await CategoryService.getCategory(formData.subcategoryId);
        if (!subcategory) throw new Error('Selected subcategory does not exist');
      }

      const discount = parseFloat(formData.discount) || 0;
      if (discount < 0 || discount > 100) throw new Error('Discount must be between 0 and 100');

      if (!formData.hasVariants) {
        if (!formData.price || parseFloat(formData.price) <= 0) {
          throw new Error('Valid price is required for products without variants');
        }
        if (!formData.sku.trim()) throw new Error('SKU is required for products without variants');
        if (!formData.warehouseAllocations || formData.warehouseAllocations.length === 0) {
          throw new Error('At least one warehouse allocation is required');
        }
        for (const allocation of formData.warehouseAllocations) {
          const warehouse = await WarehouseService.getWarehouse(allocation.warehouseId);
          if (!warehouse) throw new Error(`Warehouse ${allocation.warehouseId} does not exist`);
        }
      }

      const productRef = doc(db, PRODUCTS_COLLECTION, productId);

      let newImageUrls: string[] = [];
      if (formData.images.length > 0) {
        newImageUrls = await this.uploadImages(formData.images, productId);
      }
      const allImages = [...existingImages, ...newImageUrls].slice(0, 5);

      if (!formData.hasVariants) {
        await this.updateSimpleProductAllocations(
          productId, formData.name, formData.price, formData.sku,
          formData.warehouseAllocations, currentProduct, discount, formData.unitCost,
          formData.requiresSerialTracking
        );
      } else {
        const processedVariants = await Promise.all(
          formData.variants.map(async (variant) => {
            const variantDiscount = variant.discount || 0;
            if (variantDiscount < 0 || variantDiscount > 100) {
              throw new Error(`Variant ${variant.sku} discount must be between 0 and 100`);
            }
            if (variant.warehouseAllocations && variant.warehouseAllocations.length > 0) {
              for (const allocation of variant.warehouseAllocations) {
                const warehouse = await WarehouseService.getWarehouse(allocation.warehouseId);
                if (!warehouse) throw new Error(`Warehouse ${allocation.warehouseId} does not exist`);
              }
            }
            return {
              id: variant.id, variantSelections: variant.variantSelections,
              price: variant.price, discount: variant.discount || 0, unitCost: variant.unitCost || 0,
              sku: variant.sku, isDefault: variant.isDefault,
              minStockLevel: variant.minStockLevel, maxStockLevel: variant.maxStockLevel,
              requiresSerialTracking: variant.requiresSerialTracking || false,
            };
          })
        );
        await this.updateVariantProductAllocations(productId, formData.name, formData.variants, currentProduct);
        await updateDoc(productRef, { variants: processedVariants });
      }

      await updateDoc(productRef, {
        name: formData.name,
        description: formData.description,
        price: formData.hasVariants ? 0 : parseFloat(formData.price),
        discount: formData.hasVariants ? 0 : discount,
        unitCost: formData.hasVariants ? 0 : parseFloat(formData.unitCost) || 0,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null,
        brandId: formData.brandId || null,
        sku: formData.hasVariants ? '' : formData.sku,
        images: allImages,
        hasVariants: formData.hasVariants,
        requiresSerialTracking: formData.hasVariants ? false : (formData.requiresSerialTracking || false),
        minStockLevel: parseFloat(formData.minStockLevel) || 0,
        maxStockLevel: parseFloat(formData.maxStockLevel) || 0,
        tags: (formData.tags || []).map(t => t.trim()).filter(Boolean),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  private static async updateSimpleProductAllocations(
    productId: string, productName: string, price: string, sku: string,
    newAllocations: WarehouseAllocation[], currentProduct: Product, discount?: number, unitCost?: string,
    requiresSerialTracking?: boolean
  ): Promise<void> {
    const avgCost = parseFloat(unitCost || '') || 0;
    const warehouses = await this.getActiveWarehousesTyped();
    const currentAllocations: WarehouseAllocation[] = [];

    for (const warehouse of warehouses) {
      const inventory = await WarehouseInventoryService.getProductInventory(warehouse.id, productId);
      if (inventory) {
        currentAllocations.push({
          warehouseId: warehouse.id, warehouseName: warehouse.name,
          quantity: inventory.quantity, minStockLevel: inventory.minStockLevel,
          maxStockLevel: inventory.maxStockLevel, isLowStock: inventory.isLowStock,
          isOutOfStock: inventory.isOutOfStock, isOverStock: inventory.isOverStock,
        });
      }
    }

    const toRemove = currentAllocations.filter(
      cur => !newAllocations.some(a => a.warehouseId === cur.warehouseId)
    );
    for (const allocation of toRemove) {
      await WarehouseInventoryService.removeProductFromWarehouse(allocation.warehouseId, productId);
    }

    for (const newAllocation of newAllocations) {
      const alreadyExists = currentAllocations.some(a => a.warehouseId === newAllocation.warehouseId);
      if (alreadyExists && !requiresSerialTracking) {
        await WarehouseInventoryService.updateProductQuantity(
          newAllocation.warehouseId, productId, newAllocation.quantity, undefined, discount
        );
        await WarehouseInventoryService.updateStockLevels(
          newAllocation.warehouseId, productId, newAllocation.minStockLevel,
          newAllocation.maxStockLevel, undefined
        );
      } else {
        // Serial-tracked rows always go through allocateProductToWarehouse
        // (new or existing) since it's the one path that knows how to
        // reconcile availableSerials against the admin-entered list.
        await WarehouseInventoryService.allocateProductToWarehouse(
          newAllocation.warehouseId, productId, productName, newAllocation.quantity,
          newAllocation.minStockLevel, newAllocation.maxStockLevel, sku,
          parseFloat(price), undefined, undefined, discount, avgCost,
          requiresSerialTracking ? (newAllocation.serialNumbers || []) : undefined
        );
      }
    }
  }

  private static async updateVariantProductAllocations(
    productId: string, productName: string, newVariants: ProductVariant[], currentProduct: Product
  ): Promise<void> {
    const warehouses = await this.getActiveWarehousesTyped();

    for (const newVariant of newVariants) {
      if (!newVariant.warehouseAllocations) continue;
      const variantName = newVariant.variantSelections.map(vs => vs.variantValue).join(' - ');
      const currentAllocations: WarehouseAllocation[] = [];

      for (const warehouse of warehouses) {
        const inventory = await WarehouseInventoryService.getProductInventory(
          warehouse.id, productId, newVariant.id
        );
        if (inventory) {
          currentAllocations.push({
            warehouseId: warehouse.id, warehouseName: warehouse.name,
            quantity: inventory.quantity, minStockLevel: inventory.minStockLevel,
            maxStockLevel: inventory.maxStockLevel, isLowStock: inventory.isLowStock,
            isOutOfStock: inventory.isOutOfStock, isOverStock: inventory.isOverStock,
          });
        }
      }

      const toRemove = currentAllocations.filter(
        cur => !newVariant.warehouseAllocations!.some(a => a.warehouseId === cur.warehouseId)
      );
      for (const allocation of toRemove) {
        await WarehouseInventoryService.removeProductFromWarehouse(
          allocation.warehouseId, productId, newVariant.id
        );
      }

      for (const newAllocation of newVariant.warehouseAllocations) {
        const alreadyExists = currentAllocations.some(a => a.warehouseId === newAllocation.warehouseId);
        if (alreadyExists && !newVariant.requiresSerialTracking) {
          await WarehouseInventoryService.updateProductQuantity(
            newAllocation.warehouseId, productId, newAllocation.quantity,
            newVariant.id, newVariant.discount
          );
          await WarehouseInventoryService.updateStockLevels(
            newAllocation.warehouseId, productId, newAllocation.minStockLevel,
            newAllocation.maxStockLevel, newVariant.id
          );
        } else {
          await WarehouseInventoryService.allocateProductToWarehouse(
            newAllocation.warehouseId, productId, productName, newAllocation.quantity,
            newAllocation.minStockLevel, newAllocation.maxStockLevel, newVariant.sku,
            newVariant.price, newVariant.id, variantName, newVariant.discount, newVariant.unitCost,
            newVariant.requiresSerialTracking ? (newAllocation.serialNumbers || []) : undefined
          );
        }
      }
    }
  }

static async productHasOrders(productId: string): Promise<boolean> {
  const snap = await getDocs(
    query(collection(db, 'orders'), orderBy('orderDate', 'desc'))
  );
  return snap.docs.some(d =>
    (d.data().items ?? []).some((item: any) => item.productId === productId)
  );
}

  /* ===================== DELETE ===================== */

  static async deleteProduct(productId: string): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');

      const warehouses = await this.getActiveWarehousesTyped();

      if (!product.hasVariants) {
        await Promise.all(
          warehouses.map(w => WarehouseInventoryService.removeProductFromWarehouse(w.id, productId))
        );
      } else {
        await Promise.all(
          product.variants.flatMap(variant =>
            warehouses.map(w =>
              WarehouseInventoryService.removeProductFromWarehouse(w.id, productId, variant.id)
            )
          )
        );
      }

      if (product.images.length) await this.deleteImages(product.images);
      await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  /* ===================== READ — SINGLE ===================== */

  static async getProduct(productId: string): Promise<Product | null> {
    try {
      const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, productId));
      if (!productSnap.exists()) return null;
      const product = this.mapDocToProduct(productSnap);
      return await this.enrichProductWithInventory(product);
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  static async getProductWithDetails(productId: string): Promise<ProductWithDetails | null> {
    try {
      const product = await this.getProduct(productId);
      if (!product) return null;
      const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
      const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
      return { ...product, category: category || undefined, subcategory: subcategory || undefined };
    } catch (error) {
      console.error('Error fetching product with details:', error);
      throw error;
    }
  }

  static async getProductWithWarehouseDetails(productId: string): Promise<ProductWithDetails | null> {
    try {
      const product = await this.getProduct(productId);
      if (!product) return null;
      const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
      const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
      return { ...product, category: category || undefined, subcategory: subcategory || undefined };
    } catch (error) {
      console.error('Error fetching product with warehouse details:', error);
      throw error;
    }
  }

  /* ===================== READ — COLLECTIONS ===================== */

  /** @deprecated Use getProductsPaginated instead for better performance */
  static async getProducts(): Promise<Product[]> {
    try {
      const q = query(collection(db, PRODUCTS_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  static async getProductsWithDetails(): Promise<ProductWithDetails[]> {
    try {
      const products = await this.getProducts();
      return Promise.all(
        products.map(async product => {
          const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
          const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
          return { ...product, category: category || undefined, subcategory: subcategory || undefined };
        })
      );
    } catch (error) {
      console.error('Error fetching products with details:', error);
      throw error;
    }
  }

  static async getProductsByCategory(categoryId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('categoryId', '==', categoryId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }

  static async getProductsBySubcategory(subcategoryId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('subcategoryId', '==', subcategoryId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching products by subcategory:', error);
      throw error;
    }
  }

  static async getProductsByBrand(brandId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('brandId', '==', brandId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching products by brand:', error);
      throw error;
    }
  }

  static async getProductsByWarehouse(warehouseId: string): Promise<Product[]> {
    try {
      const inventory = await WarehouseInventoryService.getWarehouseInventory(warehouseId);
      const productIds = Array.from(new Set(inventory.map(item => item.productId)));
      const products: Product[] = [];
      for (const productId of productIds) {
        const product = await this.getProduct(productId);
        if (product) products.push(product);
      }
      return products;
    } catch (error) {
      console.error('Error fetching products by warehouse:', error);
      throw error;
    }
  }

  static async getProductsForDropdown(): Promise<Product[]> {
    try {
      const q = query(collection(db, PRODUCTS_COLLECTION), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(d => this.mapDocToProduct(d));
      if (products.length === 0) return [];
      const warehouses = await this.getActiveWarehousesTyped();
      const inventoryMap = await this.batchFetchInventory(products.map(p => p.id as string), warehouses);
      return products.map(p => this.enrichProductFromMap(p, warehouses, inventoryMap));
    } catch (error) {
      console.error('Error fetching products for dropdown:', error);
      throw error;
    }
  }

  /* ===================== SEARCH ===================== */

  static async searchProducts(searchTerm: string): Promise<Product[]> {
    try {
      const products = await this.getProducts();
      const lower = searchTerm.toLowerCase();
      return products.filter(p =>
        p.name.toLowerCase().includes(lower) ||
        p.description.toLowerCase().includes(lower) ||
        p.sku.toLowerCase().includes(lower) ||
        (p.tags || []).some(t => t.toLowerCase().includes(lower))
      );
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /* ===================== STATS =====================
   * Uses getCountFromServer() for count-based stats (zero doc reads).
   * Inventory-dependent stats still require a full read via getProducts().
   * ========================================================= */
  static async getProductStats() {
    try {
      const col = collection(db, PRODUCTS_COLLECTION);

      const [totalSnap, withVariantsSnap, withoutVariantsSnap, featuredSnap, nonFeaturedSnap, allProducts] =
        await Promise.all([
          getCountFromServer(query(col)),
          getCountFromServer(query(col, where('hasVariants', '==', true))),
          getCountFromServer(query(col, where('hasVariants', '==', false))),
          getCountFromServer(query(col, where('isFeatured', '==', true))),
          getCountFromServer(query(col, where('isFeatured', '==', false))),
          this.getProducts(),
        ]);

      const totalStock  = allProducts.reduce((sum, p) => sum + p.stockQuantity, 0);

      return {
        total:           totalSnap.data().count,
        withVariants:    withVariantsSnap.data().count,
        withoutVariants: withoutVariantsSnap.data().count,
        featured:        featuredSnap.data().count,
        nonFeatured:     nonFeaturedSnap.data().count,
        inStock:         allProducts.filter(p => !p.isOutOfStock).length,
        outOfStock:      allProducts.filter(p => p.isOutOfStock).length,
        lowStock:        allProducts.filter(p => p.isLowStock && !p.isOutOfStock).length,
        totalStock,
        categories:      new Set(allProducts.map(p => p.categoryId)).size,
        brands:          new Set(allProducts.filter(p => p.brandId).map(p => p.brandId)).size,
        averagePrice:    allProducts.length > 0
          ? allProducts.reduce((sum, p) => sum + p.price, 0) / allProducts.length
          : 0,
      };
    } catch (error) {
      console.error('Error getting product stats:', error);
      throw error;
    }
  }

  static async getWarehouseStockSummary(warehouseId: string) {
    try {
      return await WarehouseInventoryService.getWarehouseStockSummary(warehouseId);
    } catch (error) {
      console.error('Error getting warehouse stock summary:', error);
      throw error;
    }
  }

  /* ===================== STOCK UPDATES ===================== */

  static async updateProductStock(
    productId: string, quantity: number,
    operation: 'add' | 'subtract' | 'set' = 'set', warehouseId?: string
  ): Promise<void> {
    try {
      if (!warehouseId) throw new Error('Warehouse ID is required for stock updates');
      const inventory = await WarehouseInventoryService.getProductInventory(warehouseId, productId);
      if (!inventory) throw new Error('Product not found in warehouse inventory');
      let newQuantity = quantity;
      if (operation === 'add')      newQuantity = inventory.quantity + quantity;
      else if (operation === 'subtract') newQuantity = Math.max(0, inventory.quantity - quantity);
      await WarehouseInventoryService.updateProductQuantity(warehouseId, productId, newQuantity);
    } catch (error) {
      console.error('Error updating product stock:', error);
      throw error;
    }
  }

  static async updateVariantStock(
    productId: string, variantId: string, quantity: number,
    operation: 'add' | 'subtract' | 'set' = 'set', warehouseId?: string
  ): Promise<void> {
    try {
      if (!warehouseId) throw new Error('Warehouse ID is required for stock updates');
      const inventory = await WarehouseInventoryService.getProductInventory(warehouseId, productId, variantId);
      if (!inventory) throw new Error('Variant not found in warehouse inventory');
      let newQuantity = quantity;
      if (operation === 'add')      newQuantity = inventory.quantity + quantity;
      else if (operation === 'subtract') newQuantity = Math.max(0, inventory.quantity - quantity);
      await WarehouseInventoryService.updateProductQuantity(warehouseId, productId, newQuantity, variantId);
    } catch (error) {
      console.error('Error updating variant stock:', error);
      throw error;
    }
  }

  /* ===================== IMAGE REMOVAL ===================== */

  static async removeProductImage(productId: string, imageUrl: string): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');
      await this.deleteImages([imageUrl]);
      const updatedImages = product.images.filter(img => img !== imageUrl);
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        images: updatedImages, updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error removing product image:', error);
      throw error;
    }
  }

  /* ===================== PRIVATE MAP HELPER ===================== */

  private static mapDocToProduct(docSnap: QueryDocumentSnapshot<DocumentData>): Product {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || '',
      description: data.description || '',
      price: data.price || 0,
      discount: data.discount || 0,
      isOutOfStock: false,
      categoryId: data.categoryId || '',
      subcategoryId: data.subcategoryId || null,
      brandId: data.brandId || null,
      sku: data.sku || '',
      images: data.images || [],
      variants: data.variants || [],
      hasVariants: data.hasVariants || false,
      isFeatured: data.isFeatured || false,
      stockQuantity: 0,
      minStockLevel: data.minStockLevel || 0,
      maxStockLevel: data.maxStockLevel || 0,
      isLowStock: false,
      warehouseAllocations: [],
      tags: data.tags || [],
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      createdBy: data.createdBy,
    };
  }
}